import { FileTextIcon, UsersIcon, type LucideIcon } from 'lucide-react';
import {
  ApplicationDocumentIcon,
  BrokerPersonIcon,
  CalendarIcon,
  CarInsuranceIcon,
  LineChartIcon,
  LocationPinIcon,
  MapIcon,
  QuotePriceIcon,
  SignUpCalendarIcon,
  ToolboxIcon,
  type NavIcon,
} from './navIcons';

/** An icon the sidebar draws: today's icons (navIcons), and lucide's file icon for Forms Library and users icon for Portal Users. */
export type SidebarIcon = NavIcon | LucideIcon;

export type NavSubItem = { label: string; path: string; internalOnly?: boolean };

/**
 * One sidebar entry. `name` is the first path segment it navigates to and is matched against.
 * `internalOnly` means the user's roles include dunvegan:internal, which is how the Admin tab and
 * the Monthly PML Snapshot item are gated today (DashboardTabs and navVisibility.js); it is not
 * the derived isInternal flag.
 */
export type NavItem = {
  name: string;
  label: string;
  icon: SidebarIcon;
  internalOnly?: boolean;
  submenu?: NavSubItem[];
};

export const INTERNAL_ROLE = 'dunvegan:internal';

/** From this width the sidebar starts expanded; below it (and from 768px) it starts as icons (D2). */
export const EXPAND_AT = 1230;

export const navItems: NavItem[] = [
  { name: 'applications', label: 'Applications', icon: ApplicationDocumentIcon },
  { name: 'quotes', label: 'Quotes', icon: QuotePriceIcon },
  { name: 'bind-forms', label: 'Bind Forms', icon: SignUpCalendarIcon },
  { name: 'policies', label: 'Policies', icon: CarInsuranceIcon },
  { name: 'analytics', label: 'Analytics', icon: LineChartIcon },
  {
    name: 'admin',
    label: 'Admin',
    icon: ToolboxIcon,
    internalOnly: true,
    submenu: [
      { label: 'Brokers', path: '/admin/brokers' },
      { label: 'Locations', path: '/admin/locations' },
      { label: 'Monthly Reporting', path: '/admin/monthly-reporting' },
      { label: 'Forms Library', path: '/admin/forms' },
      { label: 'Portal Users', path: '/admin/portal-users' },
    ],
  },
  {
    name: 'aggregation',
    label: 'Aggregation',
    icon: MapIcon,
    submenu: [
      { label: 'Aggregation Map', path: '/aggregation' },
      { label: 'Monthly PML Snapshot', path: '/aggregation/pml-snapshot', internalOnly: true },
    ],
  },
];

/** The icon for a submenu item, as today. */
export const subItemIcons: Record<string, SidebarIcon> = {
  '/admin/brokers': BrokerPersonIcon,
  '/admin/locations': LocationPinIcon,
  '/admin/monthly-reporting': CalendarIcon,
  '/admin/forms': FileTextIcon,
  '/admin/portal-users': UsersIcon,
  '/aggregation': MapIcon,
  '/aggregation/pml-snapshot': MapIcon,
};

/** The items a user with `roles` sees, with their submenus filtered the same way. */
export const visibleNavItems = (roles: readonly string[]): NavItem[] => {
  const internal = roles.includes(INTERNAL_ROLE);
  return navItems
    .filter((item) => !item.internalOnly || internal)
    .map((item) => (item.submenu ? { ...item, submenu: item.submenu.filter((sub) => !sub.internalOnly || internal) } : item));
};

/** The label of the tab whose name is `name`, for naming the dashboard's list region. */
export const navLabelFor = (name: string | undefined): string | undefined => navItems.find((item) => item.name === name)?.label;
